import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Award } from 'lucide-react';
import { scholarshipsAPI } from '../../services/api';

const getUrgency = (deadline) => {
  if (!deadline) return { label: 'Open', bg: '#F3F4F6', color: '#4B5563' };
  const now = new Date();
  const due = new Date(deadline);
  if (isNaN(due.getTime())) return { label: 'Open', bg: '#F3F4F6', color: '#4B5563' };
  const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return { label: 'Closed', bg: '#F3F4F6', color: '#9CA3AF' };
  if (diffDays <= 30) return { label: 'Urgent', bg: '#FEE2E2', color: '#991B1B' };
  if (diffDays <= 90) return { label: 'Soon', bg: '#FEF3C7', color: '#92400E' };
  return { label: 'On Track', bg: '#DCFCE7', color: '#166534' };
};

const formatDeadline = (deadline) => {
  if (!deadline) return null;
  const d = new Date(deadline);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ScholarshipsToFocus = () => {
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    scholarshipsAPI
      .getSavedScholarships()
      .then((data) => setScholarships(Array.isArray(data) ? data : []))
      .catch(() => setScholarships([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="scholarships-to-focus">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Saved Scholarships</h2>
        <Link
          to="/scholarships"
          className="text-sm font-medium transition-colors"
          style={{ color: '#1a5d3a' }}
          data-testid="scholarships-explore-all-link"
        >
          Explore All
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 animate-pulse">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/4" />
                </div>
                <div className="h-6 bg-gray-200 rounded w-16" />
              </div>
            </div>
          ))}
        </div>
      ) : scholarships.length === 0 ? (
        <div
          className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center"
          data-testid="scholarships-empty-state"
        >
          <p className="text-sm text-gray-500 mb-4">No scholarships saved yet</p>
          <Link
            to="/scholarships"
            className="inline-block text-sm font-medium px-4 py-2 rounded-lg text-white"
            style={{ backgroundColor: '#1a5d3a' }}
            data-testid="scholarships-find-link"
          >
            Find Scholarships
          </Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="scholarships-list">
          {scholarships.slice(0, 5).map((scholarship) => {
            const urgency = getUrgency(scholarship.deadline);
            const deadlineLabel = formatDeadline(scholarship.deadline);
            return (
              <div
                key={scholarship.id || scholarship._id}
                className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-gray-200 transition-colors"
                data-testid={`scholarship-card-${scholarship.id || scholarship._id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Award size={16} style={{ color: '#1a5d3a' }} aria-hidden="true" />
                      <div className="text-sm font-semibold text-gray-900 truncate">
                        {scholarship.name}
                      </div>
                    </div>
                    {scholarship.amount && (
                      <div className="text-sm font-bold mt-1" style={{ color: '#1a5d3a' }}>
                        {scholarship.amount}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <span
                      className="inline-block px-2 py-1 text-xs font-medium rounded"
                      style={{ backgroundColor: urgency.bg, color: urgency.color }}
                    >
                      {urgency.label}
                    </span>
                    {deadlineLabel && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock size={11} aria-hidden="true" />
                        {deadlineLabel}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ScholarshipsToFocus;
